import React from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Crosshair, Camera, Check, Loader2, MapPin, ShieldAlert } from "lucide-react";

// Plain OSM tiles: key-free and detailed to z19, which a street-level survey needs.
const SURVEY_TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const SURVEY_ATTRIBUTION = "&copy; OpenStreetMap contributors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Same storage slot the Admin dashboard uses: the key is typed by the admin and
// never lives in the source.
const ADMIN_KEY_STORAGE = "spotdrop_admin_key";

const KINDS = [
  { key: "free_parking", label: "Free parking" },
  { key: "residents_only", label: "Residents only" },
  { key: "accessible_bay", label: "Blue Badge" },
  { key: "paid_parking", label: "Paid parking" },
  { key: "other_restriction", label: "Other restriction" },
];

const Recenter = ({ lat, lng }) => {
  const map = useMap();
  React.useEffect(() => {
    if (Number.isFinite(lat)) map.setView([lat, lng], Math.max(map.getZoom(), 18));
  }, [lat, lng, map]);
  return null;
};

const pin = L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:999px;background:#ccff00;border:3px solid #0a0a0c;box-shadow:0 6px 18px rgba(0,0,0,.6)"></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

export default function Survey() {
  const [adminKey, setAdminKey] = React.useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = React.useState("");
  const [pos, setPos] = React.useState(null); // {lat, lng, acc}
  const [locating, setLocating] = React.useState(false);
  const [kind, setKind] = React.useState("free_parking");
  const [form, setForm] = React.useState({ name: "", schedule: "", notes: "", restriction: "" });
  const [photo, setPhoto] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(0);

  // One-shot GPS read on demand. No watchPosition anywhere: nothing is tracked.
  const locate = React.useCallback(() => {
    if (!navigator.geolocation) return toast.error("This device has no location support.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.round(p.coords.accuracy) });
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast.error("Could not read your location. Allow location access and try again.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  React.useEffect(() => {
    if (adminKey) locate();
  }, [adminKey, locate]);

  const save = async () => {
    if (!pos) return toast.error("Capture the location first.");
    setSaving(true);
    try {
      const body = {
        kind,
        lat: Number(pos.lat.toFixed(6)),
        lng: Number(pos.lng.toFixed(6)),
        name: form.name.trim() || null,
        schedule: form.schedule.trim() || null,
        notes: [form.restriction.trim(), form.notes.trim()].filter(Boolean).join(" — ") || null,
        source: "admin_survey",
        status: "pending",
        evidence: [
          {
            source: "admin_survey",
            gps_accuracy_m: pos.acc ?? null,
            captured_at: new Date().toISOString(),
          },
        ],
      };
      const res = await axios.post(`${API}/admin/parking-places`, body, {
        headers: { "X-Admin-Key": adminKey },
      });
      const placeId = res.data?.id;
      if (photo && placeId) {
        const fd = new FormData();
        fd.append("photo", photo);
        try {
          await axios.post(`${API}/admin/parking-places/${placeId}/photo`, fd, {
            headers: { "X-Admin-Key": adminKey },
          });
        } catch {
          toast.error("Survey saved, but the sign photo could not be uploaded.");
        }
      }
      toast.success("Saved as pending for review.");
      setSaved((n) => n + 1);
      setForm({ name: "", schedule: "", notes: "", restriction: "" });
      setPhoto(null);
      locate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save this survey.");
    } finally {
      setSaving(false);
    }
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] text-white px-5 py-16 flex flex-col justify-center">
        <h1 className="font-heading text-2xl font-semibold">Survey Mode</h1>
        <p className="text-sm text-zinc-400 mt-2">Admin only. Enter your admin key.</p>
        <Input
          type="password"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder="Admin key"
          className="mt-5 h-12 bg-[#141416] border-white/10 text-white"
          data-testid="survey-key-input"
        />
        <Button
          onClick={() => {
            if (!keyInput.trim()) return;
            localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim());
            setAdminKey(keyInput.trim());
          }}
          className="mt-3 h-12 bg-[#ccff00] text-[#0a0a0c] font-semibold rounded-full"
          data-testid="survey-key-submit"
        >
          Start surveying
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white pb-32" data-testid="survey-page">
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-semibold">Survey Mode</h1>
          <p className="text-[11px] text-zinc-500">
            Saved this session: <span className="text-[#ccff00]">{saved}</span> · everything stays pending
          </p>
        </div>
        <Button
          onClick={locate}
          size="sm"
          variant="outline"
          className="h-10 rounded-full bg-transparent border-white/15 text-white"
          data-testid="survey-locate"
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
        </Button>
      </div>

      <div className="px-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 h-[240px]" data-testid="survey-map">
          {pos ? (
            <MapContainer
              center={[pos.lat, pos.lng]}
              zoom={18}
              zoomControl={false}
              style={{ height: "100%", width: "100%", background: "#0a0a0c" }}
            >
              <TileLayer url={SURVEY_TILES} attribution={SURVEY_ATTRIBUTION} maxZoom={19} />
              <Recenter lat={pos.lat} lng={pos.lng} />
              <Marker
                position={[pos.lat, pos.lng]}
                icon={pin}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const ll = e.target.getLatLng();
                    setPos((p) => ({ ...p, lat: ll.lat, lng: ll.lng, moved: true }));
                  },
                }}
              />
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-zinc-500">
              {locating ? "Reading GPS…" : "No location yet — tap the crosshair"}
            </div>
          )}
        </div>

        <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="survey-accuracy">
          <MapPin className="w-3.5 h-3.5 text-[#ccff00]" />
          {pos ? (
            <span className={pos.acc > 25 ? "text-amber-300" : "text-zinc-400"}>
              {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)} · GPS accuracy ±{pos.acc} m
              {pos.acc > 25 ? " — low accuracy, drag the pin onto the exact bay" : ""}
              {pos.moved ? " · pin moved manually" : ""}
            </span>
          ) : (
            <span className="text-zinc-500">Location not captured</span>
          )}
        </div>

        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Type</div>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="survey-kinds">
            {KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                onClick={() => setKind(k.key)}
                className={`h-12 rounded-xl text-sm font-medium border transition-colors ${
                  kind === k.key
                    ? "bg-[#ccff00] text-[#0a0a0c] border-transparent"
                    : "bg-[#141416] text-zinc-300 border-white/10"
                }`}
                data-testid={`survey-kind-${k.key}`}
              >
                {k.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name or street (optional)"
            className="h-12 bg-[#141416] border-white/10 text-white"
            data-testid="survey-name"
          />
          <Input
            value={form.restriction}
            onChange={(e) => setForm({ ...form, restriction: e.target.value })}
            placeholder="Restriction on the sign (e.g. permit holders only)"
            className="h-12 bg-[#141416] border-white/10 text-white"
            data-testid="survey-restriction"
          />
          <Input
            value={form.schedule}
            onChange={(e) => setForm({ ...form, schedule: e.target.value })}
            placeholder="Times (e.g. Mo-Fr 08:00-18:00)"
            className="h-12 bg-[#141416] border-white/10 text-white"
            data-testid="survey-schedule"
          />
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes for review"
            rows={3}
            className="bg-[#141416] border-white/10 text-white"
            data-testid="survey-notes"
          />
          <label
            className="flex items-center gap-3 h-14 px-4 rounded-xl bg-[#141416] border border-white/10 cursor-pointer"
            data-testid="survey-photo-label"
          >
            <Camera className="w-4 h-4 text-[#ccff00]" />
            <span className="text-sm text-zinc-300 truncate">
              {photo ? photo.name : "Photograph the parking sign"}
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
              data-testid="survey-photo-input"
            />
          </label>
        </div>

        <p className="mt-4 text-[11px] text-zinc-500 flex items-start gap-2">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Location is read only when you tap the crosshair — nothing is tracked. Surveys are saved as
          pending and never published automatically.
        </p>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c] to-transparent">
        <Button
          onClick={save}
          disabled={!pos || saving}
          className="w-full h-14 bg-[#ccff00] text-[#0a0a0c] font-semibold rounded-full text-base disabled:opacity-40"
          data-testid="survey-save"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
          {saving ? "" : "Save as pending"}
        </Button>
      </div>
    </div>
  );
}
